import { useState, useEffect, useCallback } from "react";
import { Sun, Moon } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";

interface Props {
  isTransparent: boolean;
  borderColor: string;
  textColor?: string;
}

const ThemeToggle = ({ isTransparent, borderColor, textColor }: Props) => {
  const { t } = useLanguage();

  const [isDark, setIsDark] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    localStorage.setItem("theme", isDark ? "dark" : "light");
  }, [isDark]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem("theme")) {
        setIsDark(e.matches);
      }
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const toggle = useCallback(() => setIsDark((v) => !v), []);

  return (
    <button
      onClick={toggle}
      className="liquid-capsule flex h-8 w-8 items-center justify-center rounded-full border border-transparent transition-all duration-200"
      style={{
        color: textColor ?? (isTransparent ? "hsl(var(--foreground) / 0.94)" : "hsl(var(--foreground) / 0.90)"),
        borderColor,
      }}
      aria-label={isDark ? t.theme.light : t.theme.dark}
      title={isDark ? t.theme.light : t.theme.dark}
    >
      {isDark ? (
        <Sun className="h-3.5 w-3.5" />
      ) : (
        <Moon className="h-3.5 w-3.5" />
      )}
    </button>
  );
};

export default ThemeToggle;
